import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, apiErr, checkoutOriginUrl, goToCheckout } from "../api";
import { isNative } from "../mobileAuth";
import { onAuthedFileClick } from "../mobileFiles";
import { useAuth } from "../AuthContext";
import { Page, SectionTitle } from "../components/Layout";
import {
  GraduationCap, ChevronLeft, FileText, Download, Upload, MessageSquare, Award,
  BookOpen, LifeBuoy, CheckCircle2, Clock, AlertCircle,
} from "lucide-react";

const BE = process.env.REACT_APP_BACKEND_URL;
const fileHref = (u) => (!u ? "#" : u.startsWith("http") ? u : `${BE}${u}`);
const inp = "w-full border border-gray-200 rounded-xl px-3 py-2 mt-1 text-sm";
const fmtD = (iso) => (iso ? new Date(iso).toLocaleDateString("it-IT") : "—");
const eur = (n) => (Number(n) > 0 ? `${Number(n)} €` : "Gratuito");
const readFileDataUrl = (file) => new Promise((res, rej) => { const r = new FileReader(); r.onload = () => res(r.result); r.onerror = rej; r.readAsDataURL(file); });

const SUB_LABEL = {
  submitted: ["Consegnato · in revisione", "bg-blue-100 text-blue-700"],
  needs_fix: ["Da correggere", "bg-amber-100 text-amber-700"],
  approved: ["Approvato", "bg-green-100 text-green-700"],
};

/* ============================ ROOT ============================ */
export default function StudentDashboard() {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const [courses, setCourses] = useState(null);
  const [selected, setSelected] = useState(null);

  useEffect(() => { if (!loading && !user) nav("/accedi"); }, [user, loading]); // eslint-disable-line
  useEffect(() => {
    if (user) api.get("/academy/my/courses").then((r) => setCourses(r.data)).catch((e) => { toast.error(apiErr(e)); setCourses([]); });
  }, [user]);

  if (loading || !user) return <Page><div className="text-center text-gray-400 pt-20">…</div></Page>;

  if (selected) return <Page><CourseView enr={selected} onBack={() => setSelected(null)} /></Page>;

  return (
    <Page>
      <SectionTitle sub="I tuoi corsi MAS Academy: materiali, esercizi, tutoraggio e attestati.">Area Allievo</SectionTitle>
      {courses === null ? <div className="text-center text-gray-400 pt-10 text-sm">Carico…</div> : (
        <div data-testid="student-courses" className="space-y-3">
          {courses.length === 0 && <p className="text-sm text-gray-400" data-testid="student-courses-empty">Non hai ancora corsi Academy attivi.</p>}
          {courses.map((c) => (
            <div key={c.enrollment_id} className="card p-4" data-testid={`student-course-${c.enrollment_id}`}>
              <div className="flex items-center gap-2 text-violet-700 mb-1"><GraduationCap size={16} /><span className="text-[11px] font-semibold">{c.branding?.provider_label || "Corso erogato da MAS Academy"}</span></div>
              <div className="font-semibold text-sm">{c.title}</div>
              <div className="text-xs text-gray-500 mt-0.5">{c.duration_days ? `${c.duration_days} giornate` : ""}{c.branding?.teacher?.display_name ? ` · Docente: ${c.branding.teacher.display_name}` : ""}</div>
              <button data-testid={`student-open-${c.enrollment_id}`} onClick={() => setSelected(c)} className="btn-primary text-xs py-1.5 px-3 mt-2">Apri corso</button>
            </div>
          ))}
        </div>
      )}
    </Page>
  );
}

/* ============================ COURSE VIEW ============================ */
function CourseView({ enr, onBack }) {
  const eid = enr.enrollment_id;
  const [d, setD] = useState(null);
  const [tab, setTab] = useState("giornate");
  useEffect(() => { api.get(`/academy/my/enrollments/${eid}`).then((r) => setD(r.data)).catch((e) => toast.error(apiErr(e))); }, [eid]);
  if (!d) return <div className="text-center text-gray-400 pt-10 text-sm">Carico…</div>;
  const cid = d.course_id;
  const TABS = [["giornate", "Giornate"], ["esercizi", "Esercizi"], ["tutoraggio", "Tutoraggio"], ["assistenza", "Assistenza"], ["attestati", "Attestati"]];
  return (
    <div data-testid="student-course-view">
      <button data-testid="student-back" onClick={onBack} className="inline-flex items-center gap-1 text-sm text-violet-700 mb-3"><ChevronLeft size={16} /> I miei corsi</button>
      <div className="card p-4 mb-3">
        <div className="flex items-center gap-2 text-violet-700 mb-1"><GraduationCap size={16} /><span className="text-[11px] font-semibold">{d.branding?.provider_label || "Corso erogato da MAS Academy"}</span></div>
        <div className="font-semibold text-sm">{d.title}</div>
        {d.branding?.teacher && (
          <div className="flex items-center gap-2 mt-2">
            {d.branding.teacher.logo_url && <img src={fileHref(d.branding.teacher.logo_url)} alt="logo docente" className="w-9 h-9 rounded-lg object-cover border" />}
            <div className="min-w-0">
              <div className="text-xs font-medium truncate">{d.branding.teacher.display_name}</div>
              {d.branding.teacher.website_url && <a href={d.branding.teacher.website_url} target="_blank" rel="noreferrer" className="text-[11px] text-violet-700 truncate block">{d.branding.teacher.website_url}</a>}
            </div>
          </div>
        )}
        {d.description && <p className="text-xs text-gray-500 mt-2">{d.description}</p>}
      </div>
      <select data-testid="student-tab-select" value={tab} onChange={(e) => setTab(e.target.value)} className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm font-medium bg-white mb-4">
        {TABS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
      </select>
      {tab === "giornate" && <DaysTab days={d.days || []} />}
      {tab === "esercizi" && <ExercisesTab cid={cid} days={d.days || []} />}
      {tab === "tutoraggio" && <TutoringTab eid={eid} />}
      {tab === "assistenza" && <AssistanceTab eid={eid} />}
      {tab === "attestati" && <CertificatesTab eid={eid} cid={cid} levelUpEnabled={d.level_up_enabled} />}
    </div>
  );
}

/* ---------- Giornate & Materiali ---------- */
function DaysTab({ days }) {
  return (
    <div data-testid="student-days">
      {days.map((d) => (
        <div key={d.id} className="card p-4 mb-3" data-testid={`student-day-${d.id}`}>
          <div className="flex items-center gap-2 text-violet-700 mb-1"><BookOpen size={14} /><span className="text-[11px] font-semibold">Giornata {(d.day_index ?? 0) + 1}</span></div>
          <div className="font-semibold text-sm">{d.title}</div>
          {d.description && <p className="text-xs text-gray-600 mt-1">{d.description}</p>}
          {d.program && <p className="text-xs text-gray-600 mt-1"><b>Programma:</b> {d.program}</p>}
          {d.instructions && <p className="text-xs text-gray-600 mt-1"><b>Istruzioni:</b> {d.instructions}</p>}
          <div className="mt-2 space-y-1">
            {(d.materials || []).map((m) => (
              <a key={m.id} href={fileHref(`/api/academy/materials/${m.id}/file`)} target="_blank" rel="noreferrer" onClick={(e) => onAuthedFileClick(e, `/api/academy/materials/${m.id}/file`, m.filename)} className="flex justify-between items-center text-[11px] bg-gray-50 rounded-lg px-2 py-1.5 text-violet-700" data-testid={`student-material-${m.id}`}>
                <span className="truncate min-w-0"><FileText size={11} className="inline mr-1" />{m.filename}</span><Download size={13} className="shrink-0" />
              </a>
            ))}
            {(d.materials || []).length === 0 && <div className="text-[11px] text-gray-400">Nessun materiale per questa giornata.</div>}
          </div>
        </div>
      ))}
      {days.length === 0 && <p className="text-sm text-gray-400">Nessuna giornata pubblicata.</p>}
    </div>
  );
}

/* ---------- Esercizi & Consegne ---------- */
function ExercisesTab({ cid, days }) {
  const [exs, setExs] = useState([]);
  const [subs, setSubs] = useState({});
  const dayLabel = (id) => { const d = days.find((x) => x.id === id); return d ? `G${(d.day_index ?? 0) + 1}` : ""; };
  const load = useCallback(async () => {
    try {
      const r = await api.get(`/academy/courses/${cid}/exercises`);
      setExs(r.data);
      const map = {};
      for (const x of r.data) { const s = await api.get(`/academy/exercises/${x.id}/submissions`); map[x.id] = s.data; }
      setSubs(map);
    } catch (e) { toast.error(apiErr(e)); }
  }, [cid]);
  useEffect(() => { load(); }, [load]);
  return (
    <div data-testid="student-exercises" className="space-y-3">
      {exs.map((x) => (
        <ExerciseCard key={x.id} ex={x} dayLabel={dayLabel(x.day_id)} subs={subs[x.id] || []} onDone={load} />
      ))}
      {exs.length === 0 && <p className="text-sm text-gray-400">Nessun esercizio assegnato.</p>}
    </div>
  );
}

function ExerciseCard({ ex, dayLabel, subs, onDone }) {
  const [text, setText] = useState("");
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const latest = subs[subs.length - 1];
  const canSubmit = !latest || latest.status === "needs_fix";
  const pick = async (e) => {
    const out = [];
    for (const f of Array.from(e.target.files || [])) {
      if (f.size > (f.type === "application/pdf" ? 20 : 5) * 1024 * 1024) { toast.error(`${f.name}: file troppo grande`); continue; }
      out.push({ data: await readFileDataUrl(f), filename: f.name });
    }
    setFiles(out); e.target.value = "";
  };
  const submit = async () => {
    if (!text.trim() && files.length === 0) return toast.error("Inserisci testo o allegati");
    setBusy(true);
    try { await api.post(`/academy/exercises/${ex.id}/submissions`, { text, attachments: files }); toast.success("Consegna inviata"); setText(""); setFiles([]); onDone(); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  return (
    <div className="card p-4" data-testid={`student-exercise-${ex.id}`}>
      <div className="flex justify-between items-start gap-2">
        <div className="min-w-0"><div className="font-semibold text-sm">{ex.title}</div><div className="text-[11px] text-gray-400">{dayLabel}{ex.required ? " · obbligatorio" : " · facoltativo"}</div></div>
        {latest && <span className={`text-[10px] px-2 py-1 rounded-full shrink-0 ${(SUB_LABEL[latest.status] || ["", "bg-gray-100"])[1]}`}>{(SUB_LABEL[latest.status] || [latest.status])[0]}</span>}
      </div>
      {ex.instructions && <p className="text-xs text-gray-600 mt-1">{ex.instructions}</p>}

      {subs.length > 0 && (
        <div className="mt-2 space-y-2" data-testid={`student-versions-${ex.id}`}>
          {subs.map((s) => (
            <div key={s.id} className="bg-gray-50 rounded-lg p-2" data-testid={`student-sub-${s.id}`}>
              <div className="text-[10px] uppercase tracking-wide text-gray-400">Versione {s.version} · {(SUB_LABEL[s.status] || [s.status])[0]} · {fmtD(s.created_at)}</div>
              {s.text && <div className="text-xs text-gray-800 mt-1 whitespace-pre-wrap">{s.text}</div>}
              {(s.attachments || []).map((a, i) => <a key={i} href={fileHref(`/api/academy/uploads/${a.upload_id}/file`)} target="_blank" rel="noreferrer" onClick={(e) => onAuthedFileClick(e, `/api/academy/uploads/${a.upload_id}/file`, a.filename)} className="text-violet-700 text-[11px] inline-flex items-center gap-1 mt-1 mr-2"><Download size={11} /> {a.filename || "allegato"}</a>)}
              {s.teacher_feedback && <div className="text-[11px] text-gray-700 mt-1 bg-white rounded p-1.5" data-testid={`student-feedback-${s.id}`}><b>Feedback docente:</b> {s.teacher_feedback}</div>}
            </div>
          ))}
        </div>
      )}

      {latest && latest.status === "approved" && <div className="text-[11px] text-green-700 mt-2 inline-flex items-center gap-1"><CheckCircle2 size={12} /> Esercizio approvato</div>}
      {canSubmit && (
        <div className="mt-2 space-y-1" data-testid={`student-submit-box-${ex.id}`}>
          {latest && latest.status === "needs_fix" && <div className="text-[11px] text-amber-700">Il docente ha richiesto una correzione: invia una nuova versione.</div>}
          <textarea data-testid={`student-submit-text-${ex.id}`} className={inp} rows={2} placeholder="La tua consegna…" value={text} onChange={(e) => setText(e.target.value)} />
          <div className="flex items-center gap-2">
            <label className="btn-outline text-xs cursor-pointer inline-flex items-center gap-1" data-testid={`student-submit-attach-${ex.id}`}><Upload size={12} /> Allega{files.length ? ` (${files.length})` : ""}<input type="file" multiple accept="application/pdf,image/png,image/jpeg,image/webp" className="hidden" onChange={pick} /></label>
            <button data-testid={`student-submit-${ex.id}`} disabled={busy} onClick={submit} className="btn-primary text-xs py-1 px-3 ml-auto">{busy ? "Invio…" : (latest ? "Invia nuova versione" : "Consegna")}</button>
          </div>
        </div>
      )}
    </div>
  );
}

/* ---------- Tutoraggio ---------- */
function TutoringTab({ eid }) {
  const [state, setState] = useState(null);
  const [text, setText] = useState("");
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const load = useCallback(async () => {
    try {
      const t = await api.get(`/academy/enrollments/${eid}/tutoring`);
      let msgs = [];
      if (t.data.thread?.id) { const m = await api.get(`/academy/tutoring/${t.data.thread.id}/messages`); msgs = m.data; }
      let assistActive = false;
      try { const a = await api.get(`/academy/my/enrollments/${eid}/assistance`); assistActive = a.data.assistance_active; } catch (_) {}
      setState({ tutoring: t.data.tutoring, thread: t.data.thread, msgs, assistActive });
    } catch (e) { toast.error(apiErr(e)); }
  }, [eid]);
  useEffect(() => { load(); }, [load]);
  const pick = async (e) => {
    const out = [];
    for (const f of Array.from(e.target.files || [])) { if (f.size > 8 * 1024 * 1024) { toast.error(`${f.name}: troppo grande`); continue; } out.push({ data: await readFileDataUrl(f), filename: f.name }); }
    setFiles(out); e.target.value = "";
  };
  const send = async () => {
    if (!text.trim() && files.length === 0) return toast.error("Messaggio vuoto");
    setBusy(true);
    try { await api.post(`/academy/tutoring/${state.thread.id}/messages`, { text, attachments: files }); setText(""); setFiles([]); load(); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  if (!state) return <div className="text-xs text-gray-400">Carico…</div>;
  if (!state.thread) return <div className="card p-4 text-sm text-gray-500">Il tutoraggio non è previsto per questo corso.</div>;
  const t = state.tutoring;
  const open = (t.enabled && t.status === "active") || state.assistActive;
  const badge = (t.enabled && t.status === "active") ? ["Tutoraggio attivo", "bg-green-100 text-green-700", CheckCircle2]
    : state.assistActive ? ["Assistenza post-corso attiva", "bg-violet-100 text-violet-700", LifeBuoy]
    : t.status === "expired" ? ["Tutoraggio scaduto", "bg-gray-100 text-gray-500", Clock]
    : t.status === "not_started" ? ["Tutoraggio non ancora iniziato", "bg-gray-100 text-gray-600", Clock]
    : ["Tutoraggio non attivo", "bg-gray-100 text-gray-500", AlertCircle];
  const BIcon = badge[2];
  return (
    <div data-testid="student-tutoring">
      <div className="flex items-center justify-between mb-2">
        <span className={`text-[11px] px-2 py-1 rounded-full inline-flex items-center gap-1 ${badge[1]}`} data-testid="student-tutoring-status"><BIcon size={12} /> {badge[0]}</span>
        {t.end_at && <span className="text-[11px] text-gray-400">Scadenza: {fmtD(t.end_at)}</span>}
      </div>
      <div className="max-h-[45vh] overflow-y-auto space-y-2 mb-2" data-testid="student-tutoring-messages">
        {(state.msgs || []).map((m) => (
          <div key={m.id} className={`text-xs rounded-xl p-2 ${m.sender_role === "student" ? "bg-violet-50 ml-6" : "bg-gray-50 mr-6"}`}>
            <div className="text-[10px] uppercase tracking-wide text-gray-400">{m.sender_role === "student" ? "Tu" : m.sender_role === "teacher" ? "Docente" : "MAS"} · {fmtD(m.created_at)}</div>
            {m.text && <div className="text-gray-800 mt-0.5 whitespace-pre-wrap">{m.text}</div>}
            {(m.attachments || []).map((a, i) => <a key={i} href={fileHref(`/api/academy/uploads/${a.upload_id}/file`)} target="_blank" rel="noreferrer" onClick={(e) => onAuthedFileClick(e, `/api/academy/uploads/${a.upload_id}/file`, a.filename)} className="text-violet-700 inline-flex items-center gap-1 mt-1"><Download size={11} /> {a.filename || "allegato"}</a>)}
          </div>
        ))}
        {(state.msgs || []).length === 0 && <div className="text-[11px] text-gray-400">Nessun messaggio. Scrivi al tuo docente.</div>}
      </div>
      {open ? (
        <div className="space-y-1">
          <textarea data-testid="student-tutoring-input" className={inp} rows={2} placeholder="Scrivi al docente…" value={text} onChange={(e) => setText(e.target.value)} />
          <div className="flex items-center gap-2">
            <label className="btn-outline text-xs cursor-pointer inline-flex items-center gap-1" data-testid="student-tutoring-attach"><Upload size={12} /> Allega{files.length ? ` (${files.length})` : ""}<input type="file" multiple accept="application/pdf,image/png,image/jpeg,image/webp" className="hidden" onChange={pick} /></label>
            <button data-testid="student-tutoring-send" disabled={busy} onClick={send} className="btn-primary text-xs py-1 px-3 ml-auto">{busy ? "Invio…" : "Invia"}</button>
          </div>
        </div>
      ) : (
        <div className="text-[11px] text-amber-700 bg-amber-50 rounded-xl p-2" data-testid="student-tutoring-closed">Tutoraggio non più attivo: puoi leggere lo storico. Per continuare a scrivere, attiva l'assistenza post-corso.</div>
      )}
    </div>
  );
}

/* ---------- Assistenza ---------- */
function AssistanceTab({ eid }) {
  const [a, setA] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => { api.get(`/academy/my/enrollments/${eid}/assistance`).then((r) => setA(r.data)).catch((e) => toast.error(apiErr(e))); }, [eid]);
  useEffect(() => { load(); }, [load]);
  const renew = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/academy/enrollments/${eid}/assistance/renew`, { origin_url: checkoutOriginUrl(), native: isNative() });
      if (r.data.free) { toast.success("Assistenza attivata"); load(); }
      else if (r.data.checkout_url) { goToCheckout(r.data.checkout_url); }
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  if (!a) return <div className="text-xs text-gray-400">Carico…</div>;
  if (!a.enabled) return <div className="card p-4 text-sm text-gray-500" data-testid="student-assistance">L'assistenza post-corso non è prevista per questo corso.</div>;
  const nowT = Date.now();
  const activeP = (a.periods || []).find((p) => (p.status === "active" || p.status === "free") && p.start_at && p.end_at && new Date(p.start_at) <= nowT && new Date(p.end_at) >= nowT);
  const upcomingP = (a.periods || []).find((p) => (p.status === "active" || p.status === "free") && p.start_at && new Date(p.start_at) > nowT);
  return (
    <div data-testid="student-assistance" className="space-y-3">
      <div className="card p-4">
        <div className="flex items-center gap-2 text-violet-700 mb-1"><LifeBuoy size={16} /><span className="text-sm font-semibold">Assistenza post-corso</span></div>
        <div className="text-xs text-gray-600">Prezzo mensile: <b>{eur(a.price)}</b></div>
        <div className="text-xs mt-1" data-testid="student-assistance-state">
          {a.assistance_active ? <span className="text-green-700">Attualmente attiva{activeP?.end_at ? ` · fino al ${fmtD(activeP.end_at)}` : ""}</span>
            : upcomingP ? <span className="text-violet-700">Attiva dal {fmtD(upcomingP.start_at)}</span>
            : <span className="text-gray-500">Non attiva</span>}
        </div>
        <button data-testid="student-assistance-renew" disabled={busy} onClick={renew} className="btn-primary text-sm w-full mt-2">{busy ? "Attendere…" : (a.price > 0 ? "Acquista / Rinnova (1 mese)" : "Attiva assistenza")}</button>
      </div>
      <div>
        <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1">Periodi</div>
        {(a.periods || []).length === 0 && <div className="text-[11px] text-gray-400">Nessun periodo di assistenza.</div>}
        {(a.periods || []).map((p) => (
          <div key={p.id} className="card p-3 mb-2" data-testid={`student-assist-period-${p.id}`}>
            <div className="flex justify-between items-center text-xs">
              <span className="text-gray-700">{fmtD(p.start_at)} → {fmtD(p.end_at)}</span>
              <span className={p.status === "active" || p.status === "free" ? "text-green-600" : p.status === "pending" ? "text-amber-600" : "text-gray-500"}>
                {p.status === "cancelled" ? "Annullato / rimborsato" : p.status === "free" ? "Gratuito attivo" : p.status === "pending" ? "In attesa di pagamento" : "Attivo"}
              </span>
            </div>
            {p.status === "cancelled" && (p.refund_amount != null) && (
              <div className="text-[11px] text-gray-500 mt-1" data-testid={`student-assist-refund-${p.id}`}>
                {Number(p.refund_amount) > 0 ? `Periodo rimborsato (${p.refund_amount} €). ` : "Periodo annullato. "}Lo storico dei messaggi resta consultabile.
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

/* ---------- Attestati ---------- */
function CertificatesTab({ eid, cid, levelUpEnabled }) {
  const [certs, setCerts] = useState([]);
  const [status, setStatus] = useState(null);
  const load = useCallback(async () => {
    try {
      const c = await api.get(`/academy/enrollments/${eid}/certificates`); setCerts(c.data);
      const s = await api.get(`/academy/courses/${cid}/exercise-status`); setStatus(s.data);
    } catch (e) { toast.error(apiErr(e)); }
  }, [eid, cid]);
  useEffect(() => { load(); }, [load]);
  const freq = certs.find((c) => c.type === "frequency");
  const lvl = certs.find((c) => c.type === "level_up");
  return (
    <div data-testid="student-certificates" className="space-y-3">
      <div className="card p-4">
        <div className="flex items-center gap-2 text-violet-700 mb-2"><Award size={16} /><span className="text-sm font-semibold">Attestato di Frequenza</span></div>
        {freq ? <a href={fileHref(freq.file_url)} target="_blank" rel="noreferrer" onClick={(e) => onAuthedFileClick(e, freq.file_url, "Attestato-di-Frequenza.pdf")} className="btn-primary text-xs py-1.5 px-3 inline-flex items-center gap-1" data-testid="student-cert-freq"><Download size={13} /> Scarica PDF</a>
          : <p className="text-xs text-gray-500">Non ancora emesso dal docente/MAS.</p>}
      </div>
      {levelUpEnabled && (
        <div className="card p-4">
          <div className="flex items-center gap-2 text-violet-700 mb-2"><Award size={16} /><span className="text-sm font-semibold">Attestato Level Up</span></div>
          {status && <div className="text-[11px] text-gray-500 mb-2">Esercizi obbligatori approvati: {status.approved_count}/{status.required_count}</div>}
          {lvl ? <a href={fileHref(lvl.file_url)} target="_blank" rel="noreferrer" onClick={(e) => onAuthedFileClick(e, lvl.file_url, "Attestato-Level-Up.pdf")} className="btn-primary text-xs py-1.5 px-3 inline-flex items-center gap-1" data-testid="student-cert-levelup"><Download size={13} /> Scarica PDF</a>
            : <p className="text-xs text-gray-500">Il Level Up viene emesso dal docente dopo l'approvazione di tutti gli esercizi obbligatori. Non è auto-assegnabile.</p>}
        </div>
      )}
    </div>
  );
}
